'use client'

import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react'

// Cache TTL: 24 hours in milliseconds
const CACHE_TTL_MS = 24 * 60 * 60 * 1000

// localStorage key prefix
const CACHE_KEY_PREFIX = 'analysis_cache'

// Periodic cleanup interval: 1 hour
const CLEANUP_INTERVAL_MS = 60 * 60 * 1000

interface AnalysisCacheEntry {
    content: string // Complete markdown content
    cachedAt: number // Unix timestamp when cached
    expiresAt: number // Unix timestamp (cachedAt + 24h)
    symbol: string
    analysisType: 'company' | 'transcript'
    metadata?: {
        fiscal_year?: number
        fiscal_period?: string
    }
}

interface AnalysisCacheMetadata {
    fiscal_year?: number
    fiscal_period?: string
}

interface AnalysisCacheContextValue {
    getCachedAnalysis: (
        type: 'company' | 'transcript',
        symbol: string,
        metadata?: AnalysisCacheMetadata
    ) => string | null
    setCachedAnalysis: (
        type: 'company' | 'transcript',
        symbol: string,
        content: string,
        metadata?: AnalysisCacheMetadata
    ) => void
    clearCache: (type?: 'company' | 'transcript', symbol?: string) => void
    isCacheAvailable: () => boolean
}

const AnalysisCacheContext = createContext<AnalysisCacheContextValue | null>(null)

/**
 * Hook to access analysis cache
 */
export function useAnalysisCache() {
    const context = useContext(AnalysisCacheContext)
    if (!context) {
        throw new Error('useAnalysisCache must be used within AnalysisCacheProvider')
    }
    return context
}

/**
 * Generates a cache key from parameters
 */
function generateCacheKey(
    type: 'company' | 'transcript',
    symbol: string,
    metadata?: AnalysisCacheMetadata
): string {
    const normalizedSymbol = symbol.toUpperCase().trim()

    if (type === 'company') {
        return `${CACHE_KEY_PREFIX}:company:${normalizedSymbol}`
    }

    // For transcript, include fiscal_year and fiscal_period if provided
    if (metadata?.fiscal_year && metadata?.fiscal_period) {
        return `${CACHE_KEY_PREFIX}:transcript:${normalizedSymbol}:${metadata.fiscal_year}:${metadata.fiscal_period}`
    }

    // Default transcript key without specific period
    return `${CACHE_KEY_PREFIX}:transcript:${normalizedSymbol}`
}

/**
 * Checks if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
    try {
        const test = '__localStorage_test__'
        localStorage.setItem(test, test)
        localStorage.removeItem(test)
        return true
    } catch {
        return false
    }
}

/**
 * Checks if a cache entry is still valid (not expired)
 */
function isCacheEntryValid(entry: AnalysisCacheEntry): boolean {
    return Date.now() < entry.expiresAt
}

/**
 * Provider component for analysis caching
 */
export function AnalysisCacheProvider({ children }: { children: React.ReactNode }) {
    const isAvailable = useRef(isLocalStorageAvailable())
    const cleanupIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined)

    /**
     * Cleanup expired cache entries
     */
    const cleanupExpiredEntries = useCallback(() => {
        if (!isAvailable.current) return

        try {
            const keysToDelete: string[] = []

            // Iterate through all localStorage keys
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                    try {
                        const entry = JSON.parse(
                            localStorage.getItem(key) || '{}'
                        ) as AnalysisCacheEntry
                        if (!isCacheEntryValid(entry)) {
                            keysToDelete.push(key)
                        }
                    } catch {
                        // Corrupted entry, mark for deletion
                        keysToDelete.push(key)
                    }
                }
            }

            // Delete expired entries
            keysToDelete.forEach((key) => localStorage.removeItem(key))

            if (keysToDelete.length > 0) {
                console.log(`[AnalysisCache] Cleaned up ${keysToDelete.length} expired entries`)
            }
        } catch (error) {
            console.error('[AnalysisCache] Cleanup error:', error)
        }
    }, [])

    /**
     * Get cached analysis if valid
     */
    const getCachedAnalysis = useCallback(
        (
            type: 'company' | 'transcript',
            symbol: string,
            metadata?: AnalysisCacheMetadata
        ): string | null => {
            if (!isAvailable.current) return null

            const cacheKey = generateCacheKey(type, symbol, metadata)

            try {
                const cached = localStorage.getItem(cacheKey)
                if (!cached) return null

                const entry = JSON.parse(cached) as AnalysisCacheEntry

                // Check if cache is still valid
                if (!isCacheEntryValid(entry)) {
                    // Expired, remove it
                    localStorage.removeItem(cacheKey)
                    return null
                }

                console.log(
                    `[AnalysisCache] Cache HIT for ${cacheKey} (age: ${Math.round((Date.now() - entry.cachedAt) / 1000 / 60)} minutes)`
                )
                return entry.content
            } catch (error) {
                console.error('[AnalysisCache] Error reading cache:', error)
                // Corrupted cache entry, remove it
                try {
                    localStorage.removeItem(cacheKey)
                } catch {
                    // Ignore
                }
                return null
            }
        },
        []
    )

    /**
     * Set cached analysis with 24h TTL
     */
    const setCachedAnalysis = useCallback(
        (
            type: 'company' | 'transcript',
            symbol: string,
            content: string,
            metadata?: AnalysisCacheMetadata
        ): void => {
            if (!isAvailable.current) return

            const cacheKey = generateCacheKey(type, symbol, metadata)
            const now = Date.now()

            const entry: AnalysisCacheEntry = {
                content,
                cachedAt: now,
                expiresAt: now + CACHE_TTL_MS,
                symbol: symbol.toUpperCase().trim(),
                analysisType: type,
                metadata,
            }

            try {
                localStorage.setItem(cacheKey, JSON.stringify(entry))
                console.log(`[AnalysisCache] Cached ${cacheKey} (expires in 24h)`)
            } catch (error) {
                // Handle QuotaExceededError
                if (error instanceof Error && error.name === 'QuotaExceededError') {
                    console.warn('[AnalysisCache] Storage quota exceeded, cleaning up...')
                    cleanupExpiredEntries()

                    // Try again after cleanup
                    try {
                        localStorage.setItem(cacheKey, JSON.stringify(entry))
                        console.log(`[AnalysisCache] Cached ${cacheKey} after cleanup`)
                    } catch (retryError) {
                        console.error('[AnalysisCache] Failed to cache after cleanup:', retryError)
                    }
                } else {
                    console.error('[AnalysisCache] Error setting cache:', error)
                }
            }
        },
        [cleanupExpiredEntries]
    )

    /**
     * Clear cache entries
     * If no parameters: clear all
     * If type only: clear all of that type
     * If type and symbol: clear specific entry
     */
    const clearCache = useCallback((type?: 'company' | 'transcript', symbol?: string): void => {
        if (!isAvailable.current) return

        try {
            if (!type) {
                // Clear all cache entries
                const keysToDelete: string[] = []
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i)
                    if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                        keysToDelete.push(key)
                    }
                }
                keysToDelete.forEach((key) => localStorage.removeItem(key))
                console.log(`[AnalysisCache] Cleared all cache (${keysToDelete.length} entries)`)
            } else if (!symbol) {
                // Clear all entries of specific type
                const keysToDelete: string[] = []
                const prefix = `${CACHE_KEY_PREFIX}:${type}:`
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i)
                    if (key && key.startsWith(prefix)) {
                        keysToDelete.push(key)
                    }
                }
                keysToDelete.forEach((key) => localStorage.removeItem(key))
                console.log(
                    `[AnalysisCache] Cleared ${type} cache (${keysToDelete.length} entries)`
                )
            } else {
                // Clear specific entry
                const cacheKey = generateCacheKey(type, symbol)
                localStorage.removeItem(cacheKey)
                console.log(`[AnalysisCache] Cleared cache for ${cacheKey}`)
            }
        } catch (error) {
            console.error('[AnalysisCache] Error clearing cache:', error)
        }
    }, [])

    /**
     * Check if cache is available
     */
    const isCacheAvailableFunc = useCallback((): boolean => {
        return isAvailable.current
    }, [])

    // Set up periodic cleanup
    useEffect(() => {
        if (!isAvailable.current) {
            console.warn('[AnalysisCache] localStorage not available, caching disabled')
            return
        }

        // Run initial cleanup
        cleanupExpiredEntries()

        // Set up periodic cleanup
        cleanupIntervalRef.current = setInterval(cleanupExpiredEntries, CLEANUP_INTERVAL_MS)

        return () => {
            if (cleanupIntervalRef.current) {
                clearInterval(cleanupIntervalRef.current)
            }
        }
    }, [cleanupExpiredEntries])

    const contextValue: AnalysisCacheContextValue = {
        getCachedAnalysis,
        setCachedAnalysis,
        clearCache,
        isCacheAvailable: isCacheAvailableFunc,
    }

    return (
        <AnalysisCacheContext.Provider value={contextValue}>
            {children}
        </AnalysisCacheContext.Provider>
    )
}
